import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { BASE_URL, ENDPOINTS } from '../config/api';
import { QRCodeSVG } from 'qrcode.react';
import './Home.css';

const Home = () => {
    const navigate = useNavigate();
    const [producteurs, setProducteurs] = useState([]);
    const [selectedProducteur, setSelectedProducteur] = useState('');
    const [matieres, setMatieres] = useState([]);
    const [selectedMatiere, setSelectedMatiere] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMatieres, setLoadingMatieres] = useState(false);
    const [error, setError] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [qrData, setQrData] = useState('');
    const [position, setPosition] = useState(null);
    const [locationError, setLocationError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchProducteurs = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/');
                return;
            }

            setIsLoading(true);
            setError('');

            try {
                const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTEURS}`, {
                    headers: {
                        'Authorization': authService.getToken()
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data) && data.length > 0) {
                        setProducteurs(data);
                        // Récupérer le producteur sauvegardé s'il existe
                        const savedProducteurId = localStorage.getItem('selectedProducteurId');
                        const savedMatiereId = localStorage.getItem('selectedMatiereId');
                        const wasConfirmed = localStorage.getItem('isConfirmed') === 'true';
                        let hasSingleMatiere = false;
                        
                        if (savedProducteurId) {
                            setSelectedProducteur(savedProducteurId);
                            // Charger les matières pour ce producteur
                            const matieresResponse = await fetch(`${BASE_URL}${ENDPOINTS.MATIERES}?idp=${savedProducteurId}`, {
                                headers: {
                                    'Authorization': authService.getToken()
                                }
                            });
                            if (matieresResponse.ok) {
                                const matieresData = await matieresResponse.json();
                                if (Array.isArray(matieresData) && matieresData.length > 0) {
                                    setMatieres(matieresData);
                                    if (matieresData.length === 1) {
                                        setSelectedMatiere(matieresData[0].IDMatiere);
                                        hasSingleMatiere = true;
                                    } else if (savedMatiereId) {
                                        setSelectedMatiere(savedMatiereId);
                                    }
                                }
                            }
                        }
                        
                        // Restaurer l'état de confirmation
                        if (wasConfirmed && savedProducteurId && (savedMatiereId || hasSingleMatiere)) {
                            setIsConfirmed(true);
                        }
                    } else {
                        setError("Aucun producteur disponible");
                    }
                } else if (response.status === 401) {
                    authService.removeToken();
                    navigate('/');
                } else {
                    setError("Erreur lors du chargement des producteurs");
                }
            } catch (error) {
                setError("Erreur de connexion au serveur");
            } finally {
                setLoading(false);
            }
        };

        fetchProducteurs();
    }, [navigate]);

    // Effet pour charger les matières quand un producteur est sélectionné
    useEffect(() => {
        const fetchMatieres = async () => {
            if (!selectedProducteur) {
                setMatieres([]);
                setSelectedMatiere('');
                return;
            }

            setLoadingMatieres(true);
            try {
                const response = await fetch(`${BASE_URL}${ENDPOINTS.MATIERES}?idp=${selectedProducteur}`, {
                    headers: {
                        'Authorization': authService.getToken()
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setMatieres(data);
                        setError('');
                    } else {
                        setError("Format de données incorrect pour les matières");
                    }
                } else if (response.status === 401) {
                    authService.removeToken();
                    navigate('/');
                } else {
                    setError("Erreur lors du chargement des matières");
                }
            } catch (error) {
                console.error('Erreur lors de la requête des matières:', error);
                setError("Erreur de connexion au serveur");
            } finally {
                setLoadingMatieres(false);
            }
        };

        fetchMatieres();
    }, [selectedProducteur, navigate]);

    // Effet pour la géolocalisation
    useEffect(() => {
        if (isConfirmed) {
            if ("geolocation" in navigator) {
                const geoOptions = {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                };

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setPosition({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        });
                        setLocationError('');
                    },
                    (error) => {
                        setLocationError("Impossible d'obtenir la position");
                        setPosition(null);
                    },
                    geoOptions
                );
            } else {
                setLocationError("La géolocalisation n'est pas supportée par votre navigateur");
            }
        }
    }, [isConfirmed]);

    const handleLogout = () => {
        authService.removeToken();
        authService.removeBadgeNumber();
        localStorage.removeItem('selectedProducteurId');
        localStorage.removeItem('selectedMatiereId');
        localStorage.removeItem('isConfirmed');
        navigate('/');
    };

    const handleProducteurChange = async (event) => {
        const selectedId = event.target.value;
        setSelectedProducteur(selectedId);
        setSelectedMatiere('');
        setLoadingMatieres(true);
        setError('');

        try {
            const response = await fetch(`${BASE_URL}${ENDPOINTS.MATIERES}?idp=${selectedId}`, {
                headers: {
                    'Authorization': authService.getToken()
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    setMatieres(data);
                    // Si une seule matière, la sélectionner automatiquement
                    if (data.length === 1) {
                        setSelectedMatiere(data[0].IDMatiere);
                    }
                } else {
                    setError("Aucune matière disponible pour ce producteur");
                }
            } else if (response.status === 401) {
                authService.removeToken();
                navigate('/');
            } else {
                setError("Erreur lors du chargement des matières");
            }
        } catch (error) {
            setError("Erreur de connexion au serveur");
        } finally {
            setLoadingMatieres(false);
        }
    };

    const handleMatiereChange = (event) => {
        const selectedId = event.target.value;
        console.log('Matière sélectionnée:', selectedId);
        setSelectedMatiere(selectedId);
        localStorage.setItem('selectedMatiereId', selectedId);
    };

    const handleConfirmation = () => {
        if (!selectedProducteur || !selectedMatiere) {
            setError("Veuillez sélectionner un producteur et une matière");
            return;
        }

        const badgeNumber = authService.getBadgeNumber();
        if (!badgeNumber) {
            setError("Numéro de badge non trouvé dans le token");
            return;
        }

        setIsConfirmed(true);
        localStorage.setItem('isConfirmed', 'true');
        setError('');
    };

    // Effet pour mettre à jour le QR code avec la position
    useEffect(() => {
        if (isConfirmed && selectedProducteur && selectedMatiere) {
            const badgeNumber = authService.getBadgeNumber();
            let data = `${badgeNumber}|${selectedProducteur}|${selectedMatiere}`;
            
            if (position) {
                data += `|${position.latitude}|${position.longitude}`;
            }
            
            setQrData(data);
        }
    }, [isConfirmed, selectedProducteur, selectedMatiere, position]);

    const handleReset = () => {
        setIsConfirmed(false);
        localStorage.removeItem('isConfirmed');
        setQrData('');
        setSelectedMatiere('');
        setSelectedProducteur('');
        localStorage.removeItem('selectedProducteurId');
        localStorage.removeItem('selectedMatiereId');
        // Le token n'est pas supprimé pour conserver le numéro de badge
    };

    const formatBadgeNumber = (badgeNumber) => {
        if (!badgeNumber) return '';
        return badgeNumber.replace(/(\d{2})(\d{3})/, '$1 $2');
    };

    console.log('Rendu du composant - loading:', loading, 'producteurs:', producteurs);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Chargement des producteurs...</p>
            </div>
        );
    }

    return (
        <div className="home-container">
            <div className="header">
                <h1>SPIN-LITE</h1>
                <button onClick={handleLogout} className="logout-button">
                    Déconnexion
                </button>
            </div>
            
            <div className="content">
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Chargement des données...</p>
                    </div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <>
                        <div className="badge-display">
                            <span className="badge-label">Badge :</span>
                            <span className="badge-number">{formatBadgeNumber(authService.getBadgeNumber())}</span>
                        </div>

                        {!isConfirmed ? (
                            <>
                                <div className={`producteur-selector ${selectedProducteur ? 'selected' : ''}`}>
                                    <label htmlFor="producteur">Sélectionnez un producteur :</label>
                                    <select 
                                        id="producteur" 
                                        value={selectedProducteur} 
                                        onChange={handleProducteurChange}
                                        className="select-input"
                                        disabled={isConfirmed}
                                    >
                                        <option value="">-- Choisir un producteur --</option>
                                        {producteurs && producteurs.length > 0 ? (
                                            producteurs.map(producteur => (
                                                <option 
                                                    key={producteur.IDProducteur} 
                                                    value={producteur.IDProducteur}
                                                >
                                                    {producteur.ChronoExterne} - {producteur.RaisonSociale}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="" disabled>Aucun producteur disponible</option>
                                        )}
                                    </select>
                                </div>

                                {selectedProducteur && (
                                    <div className={`matiere-selector ${selectedMatiere ? 'selected' : ''}`}>
                                        {loadingMatieres ? (
                                            <div className="loading-container">
                                                <div className="loading-spinner"></div>
                                                <p>Chargement des matières...</p>
                                            </div>
                                        ) : matieres.length === 1 ? (
                                            <>
                                                <label>Matière disponible :</label>
                                                <div className="single-matiere">
                                                    {matieres[0].Ancien_code} - {matieres[0].Lib_matiere}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <label htmlFor="matiere">Sélectionnez une matière :</label>
                                                <select 
                                                    id="matiere" 
                                                    value={selectedMatiere} 
                                                    onChange={handleMatiereChange}
                                                    className="select-input"
                                                    disabled={isConfirmed}
                                                >
                                                    <option value="">-- Choisir une matière --</option>
                                                    {matieres.map(matiere => (
                                                        <option 
                                                            key={matiere.IDMatiere} 
                                                            value={matiere.IDMatiere}
                                                        >
                                                            {matiere.Ancien_code} - {matiere.Lib_matiere}
                                                        </option>
                                                    ))}
                                                </select>
                                            </>
                                        )}
                                    </div>
                                )}

                                {(selectedProducteur && (selectedMatiere || matieres.length === 1)) && (
                                    <button 
                                        onClick={handleConfirmation}
                                        className="confirm-button"
                                    >
                                        Confirmer la sélection
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="qr-container">
                                {qrData && (
                                    <>
                                        <div className="qr-code">
                                            {qrData && <QRCodeSVG value={qrData} size={256} />}
                                        </div>
                                        <div className="qr-data">
                                            {qrData}
                                        </div>
                                        <div className="instruction-box">
                                            <p className="badge-instruction">
                                                Merci de présenter le Qr Code à la borne
                                            </p>
                                        </div>
                                    </>
                                )}
                                {locationError && (
                                    <div className="warning-message">
                                        {locationError}
                                    </div>
                                )}
                                <button onClick={handleReset} className="reset-button">
                                    Nouvelle sélection
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Home; 